// Creature genome: the seeded, deterministic description of one individual creature.
// Pure data and pure functions only (no Pixi) so it can be unit-tested in node.
//
// generateGenome(species, seed, traits?, form?) — same inputs → deep-equal output.
//
// WHAT DECIDES WHAT
//  - species  → body-plan family, colour family (SPECIES_COLORS), behaviours (claws snap, …)
//  - form     → which body plan of the family (see FORMS below); chosen from seed + traits
//  - seed     → individual variation: proportions, colour nudge, markings, horn/ear/tail kinds
//  - traits   → (from the sound, see traits.ts) systematic, monotonic appearance changes:
//
// TRAIT RULES (all monotonic in the trait for a fixed seed)
//  size         sizeScale = size (0.7–1.4). ProceduralCreature scales its body height by it;
//               callers can use it for layout. Outline width does NOT scale (stays bold & even).
//  stature      bodyAspect × (1 − 0.3·stature)  (tall = slim, short = wide)
//               heightScale = 1 + 0.17·stature   (taller / squatter body)
//               limbLength  = 1 + 0.4·stature    (longer legs / neck / stalks)
//  colorfulness palette = floor(colorfulness·3.5) extra Okabe-Ito accent hues (0–3, chosen away
//               from the species hue); < 0.15 → single flat colour (no markings, no blush,
//               belly barely tinted); more spots with more colour; ≥ 0.45 multi-colour
//               markings (spots/stripes cycle the palette), coloured cheeks; crest/shell/petals
//               use palette colours.
//  spikiness    spikes along the back (0, or 2–7 when ≥ 0.35), spike horns ≥ 0.6, pointy ears
//               ≥ 0.65, fangs ≥ 0.55.
//  fuzz         fuzz ≥ 0.25 → scalloped fluffy outline (bump size grows with fuzz), head tufts.
//  brightness   eye size × (0.8 + 0.5·brightness); sparkles 0–3 (≥ 0.35 / 0.6 / 0.82);
//               very dark sounds (< 0.25) get sleepy half-lids (lidRest).
//  tail         tailLength = 0.6 + tail (tails, tendrils, antennae, stingers, cables);
//               tail ≥ 0.65 grows a tail on forms that may have none; Glimmer tendrils + 0–2.
//  width        span = 0.75 + 0.7·width (wings, ears, claws, flippers, arms).
//  glow         aura = glow → halo strength at rest.
//  wobble       wobble = jelly idle squash amplitude (slow, follows the breath).
// FUN COMBOS
//  colorfulness ≥ 0.65 & glow ≥ 0.5  → shimmer: the halo slowly cycles through the palette.
//  spikiness ≥ 0.7  & fuzz ≥ 0.5     → punk mohawk in palette colours.
//
// FORM RULE: each form has trait affinities. weight = max(0.15, 1 + 3·Σ aff·(trait − neutral)),
// then one seeded weighted pick (its own RNG stream, one draw). Neutral traits → equal odds, so
// every seed range shows every form; strong traits steer it (fuzz → owl/yeti, spikiness →
// golem/scorpion/dragon, glow → fairy/ghost/wisp, tail → scorpion/octopus/jellyfish, …).
// Synth species have a single form named after the instrument, except Boomer (808 drum machine):
//   boomer       drum machine — affinities: brightness, spikiness, stature (snappy, upright)
//   boomer_bass  808 bass     — affinities: size, wobble, low brightness, short stature (heavy)
import type { Species } from "@eb/shared";
import { Rng } from "../lib/rng";
import { OKABE_ITO, SPECIES_COLORS, SYNTH_SPECIES, hueShift, mix } from "../palette";
import { NEUTRAL_TRAITS, type CreatureTraits } from "./traits";

export type MarkingKind = "none" | "spots" | "stripes" | "patch";
export type HornKind = "none" | "nubs" | "spike" | "curl";
export type TailKind = "none" | "tuft" | "fan" | "curl" | "stinger";
export type EarKind = "none" | "round" | "pointy" | "fin";

export type CreatureForm =
  | "beast"
  | "frog"
  | "golem"
  | "crab"
  | "scorpion"
  | "dragon"
  | "songbird"
  | "owl"
  | "fairy"
  | "seal"
  | "penguin"
  | "yeti"
  | "jelly"
  | "octopus"
  | "ghost"
  | "jellyfish"
  | "firefly"
  | "wisp"
  | "bug"
  | "snail"
  | "mushroom"
  | "woodwump"
  | "acidmite"
  | "choruspede"
  | "operatoad"
  | "drawbear"
  | "boomer"
  | "boomer_bass";

interface FormDef {
  form: CreatureForm;
  fantasy: boolean;
  aff: Partial<Record<keyof CreatureTraits, number>>;
}

/** Forms per species with their trait affinities (see FORM RULE above). First = classic form. */
export const FORMS: Record<Species, readonly FormDef[]> = {
  thumper: [
    { form: "beast", fantasy: false, aff: { stature: -0.5, wobble: 0.5 } },
    { form: "frog", fantasy: false, aff: { brightness: 0.5, width: 0.5, stature: 0.2 } },
    { form: "golem", fantasy: true, aff: { spikiness: 1, fuzz: -0.4 } },
  ],
  snapper: [
    { form: "crab", fantasy: false, aff: { width: 0.8 } },
    { form: "scorpion", fantasy: false, aff: { tail: 0.9, spikiness: 0.4 } },
    { form: "dragon", fantasy: true, aff: { spikiness: 0.6, glow: 0.6, colorfulness: 0.3 } },
  ],
  chirper: [
    { form: "songbird", fantasy: false, aff: { colorfulness: 0.6, brightness: 0.4 } },
    { form: "owl", fantasy: false, aff: { fuzz: 1, brightness: -0.5 } },
    { form: "fairy", fantasy: true, aff: { glow: 1, width: 0.4 } },
  ],
  clapper: [
    { form: "seal", fantasy: false, aff: { wobble: 0.6, stature: -0.4 } },
    { form: "penguin", fantasy: false, aff: { stature: 0.9 } },
    { form: "yeti", fantasy: true, aff: { fuzz: 1.1, size: 0.5 } },
  ],
  wobbler: [
    { form: "jelly", fantasy: false, aff: { wobble: 0.8 } },
    { form: "octopus", fantasy: false, aff: { tail: 0.8, colorfulness: 0.4 } },
    { form: "ghost", fantasy: true, aff: { glow: 1, fuzz: -0.3 } },
  ],
  glimmer: [
    { form: "jellyfish", fantasy: false, aff: { tail: 0.8 } },
    { form: "firefly", fantasy: false, aff: { brightness: 0.8, spikiness: 0.2 } },
    { form: "wisp", fantasy: true, aff: { glow: 0.6, colorfulness: 0.6, spikiness: 0.3 } },
  ],
  critter: [
    { form: "bug", fantasy: false, aff: { spikiness: 0.6 } },
    { form: "snail", fantasy: false, aff: { wobble: 0.7, tail: 0.3, spikiness: -0.5 } },
    { form: "mushroom", fantasy: true, aff: { colorfulness: 0.4, glow: 0.5, fuzz: 0.3 } },
  ],
  woodwump: [{ form: "woodwump", fantasy: false, aff: {} }],
  acidmite: [{ form: "acidmite", fantasy: false, aff: {} }],
  choruspede: [{ form: "choruspede", fantasy: false, aff: {} }],
  operatoad: [{ form: "operatoad", fantasy: false, aff: {} }],
  drawbear: [{ form: "drawbear", fantasy: false, aff: {} }],
  boomer: [
    { form: "boomer", fantasy: false, aff: { brightness: 0.6, spikiness: 0.4, stature: 0.3 } },
    { form: "boomer_bass", fantasy: false, aff: { size: 0.8, wobble: 0.6, brightness: -0.6, stature: -0.3 } },
  ],
};

/** All forms of a species (classic first). */
export function formsOf(species: Species): CreatureForm[] {
  return FORMS[species].map((f) => f.form);
}

/** A spot or patch in normalized body coordinates: x,y in -1..1 relative to the body ellipse, r as a fraction of body height. */
export interface MarkSpot {
  x: number;
  y: number;
  r: number;
}

export interface Markings {
  kind: MarkingKind;
  /** Spots (kind "spots", 2..9) or a single patch (kind "patch", length 1). Empty otherwise. */
  spots: MarkSpot[];
  /** Stripe count for kind "stripes" (2..5), 0 otherwise. */
  stripes: number;
  /** Stripe tilt in radians (-0.35..0.35). */
  stripeTilt: number;
  /** Markings cycle through the palette colours instead of the accent colour. */
  multi: boolean;
}

export interface CreatureGenome {
  species: Species;
  /** Body plan within the species family. */
  form: CreatureForm;
  seed: number;
  /** The traits this genome was generated from. */
  traits: CreatureTraits;
  /** Overall body scale (from traits.size). ProceduralCreature applies it; use it for layout. */
  sizeScale: number;
  /** Body height multiplier from stature, 0.83..1.17. */
  heightScale: number;
  /** Leg / neck / stalk length multiplier from stature, 0.6..1.4. */
  limbLength: number;
  /** Wing / ear / arm / claw span multiplier from width, 0.75..1.45. */
  span: number;
  /** Tail / tendril / antenna length multiplier, 0.6..1.6. */
  tailLength: number;
  /** Main body fill (within the species color family). */
  baseColor: number;
  /** Markings, horns, claw tips, tendrils. Darker/related shade of the base. */
  accentColor: number;
  /** Belly / face patch, a lighter tint of the base. */
  bellyColor: number;
  /** 0–3 extra Okabe-Ito accent colours (colorfulness). */
  palette: number[];
  /** Cheek blush colour (pink, or a palette colour for colourful creatures). */
  cheekColor: number;
  /** Body width / height ratio (incl. stature), see GENOME_RANGES. */
  bodyAspect: number;
  /** Eye radius as a fraction of body height (incl. brightness). */
  eyeSize: number;
  /** Distance between the eye centres as a fraction of body width, 0.18..0.5. */
  eyeSpacing: number;
  /** Vertical eye offset as a fraction of body height (negative = higher), -0.08..0.08. */
  eyeHeight: number;
  /** Pupil radius as a fraction of the eye radius, 0.38..0.7. */
  pupilSize: number;
  /** 1 (cyclops, rare) or 2. */
  eyeCount: 1 | 2;
  /** Eye highlight sparkles 0..3 (brightness). */
  sparkles: number;
  /** Resting eyelid closure 0..0.35 (dark, dull sounds look sleepy). */
  lidRest: number;
  /** Mouth width as a fraction of body width, 0.18..0.7. */
  mouthWidth: number;
  /** Little fangs (spikiness). */
  teeth: boolean;
  /** Cheek blush dots. */
  blush: boolean;
  markings: Markings;
  horns: HornKind;
  /** Spikes along the back, 0..7 (spikiness). */
  spikes: number;
  /** 0..1, fluffy scalloped outline when ≥ 0.25. */
  fuzz: number;
  /** Punk mohawk (very spiky + fuzzy). */
  mohawk: boolean;
  /** Antenna count 0..3 (Critters always have at least 1). */
  antennae: number;
  tail: TailKind;
  ears: EarKind;
  /** Snapper claw size multiplier 0.75..1.35 (1 for other species). */
  clawSize: number;
  /** Beak length multiplier 0.6..1.5 (Chirper, 1 for other species). */
  beakLength: number;
  /** Glimmer tendril count 3..7 (0 for other species). */
  tendrils: number;
  /** Leg pairs: Critter 2..3, Snapper 2..3, Thumper/Chirper 1, others 0. */
  legPairs: number;
  /** Resting halo strength 0..1. */
  aura: number;
  /** Jelly idle wobble amplitude 0..1. */
  wobble: number;
  /** Halo slowly cycles through the palette (colourful + glowing). */
  shimmer: boolean;
}

/** Documented numeric ranges (inclusive), used by tests and by anyone tweaking the generator. */
export const GENOME_RANGES = {
  bodyAspect: [0.5, 2.15],
  eyeSize: [0.06, 0.23],
  eyeSpacing: [0.18, 0.5],
  eyeHeight: [-0.08, 0.08],
  pupilSize: [0.38, 0.7],
  mouthWidth: [0.18, 0.7],
  antennae: [0, 3],
  clawSize: [0.75, 1.35],
  beakLength: [0.6, 1.5],
  tendrils: [3, 7],
  legPairs: [0, 3],
  sizeScale: [0.7, 1.4],
  heightScale: [0.83, 1.17],
  limbLength: [0.6, 1.4],
  span: [0.75, 1.45],
  tailLength: [0.6, 1.6],
  spikes: [0, 7],
  palette: [0, 3],
  /** Max hue distance (degrees) of baseColor from the species' canonical color. */
  baseHueTolerance: 16,
} as const;

/** Per-species body plan ranges. */
interface Plan {
  aspect: [number, number];
  mouth: [number, number];
  spacing: [number, number];
  eyeSize: [number, number];
  horns: HornKind[];
  tails: TailKind[];
  ears: EarKind[];
  antennae: [number, number];
  legPairs: [number, number];
  markings: MarkingKind[];
}

const PLANS: Record<Species, Plan> = {
  thumper: {
    aspect: [1.1, 1.4],
    mouth: [0.45, 0.7],
    spacing: [0.3, 0.46],
    eyeSize: [0.09, 0.13],
    horns: ["none", "none", "nubs", "spike", "curl"],
    tails: ["none", "tuft", "curl"],
    ears: ["none", "round", "pointy"],
    antennae: [0, 0],
    legPairs: [1, 1],
    markings: ["none", "spots", "stripes", "patch"],
  },
  snapper: {
    aspect: [1.3, 1.6],
    mouth: [0.18, 0.3],
    spacing: [0.22, 0.4],
    eyeSize: [0.12, 0.16],
    horns: ["none", "nubs", "spike"],
    tails: ["none"],
    ears: ["none"],
    antennae: [0, 2],
    legPairs: [2, 3],
    markings: ["none", "spots", "stripes"],
  },
  chirper: {
    aspect: [0.85, 1.1],
    mouth: [0.2, 0.3],
    spacing: [0.3, 0.46],
    eyeSize: [0.12, 0.17],
    horns: ["none", "nubs", "spike", "curl"],
    tails: ["tuft", "fan", "curl"],
    ears: ["none"],
    antennae: [0, 0],
    legPairs: [1, 1],
    markings: ["none", "spots", "patch"],
  },
  clapper: {
    aspect: [0.8, 1.0],
    mouth: [0.22, 0.38],
    spacing: [0.3, 0.46],
    eyeSize: [0.1, 0.14],
    horns: ["none", "none", "nubs"],
    tails: ["none", "fan"],
    ears: ["none", "round"],
    antennae: [0, 0],
    legPairs: [0, 0],
    markings: ["none", "spots", "patch"],
  },
  wobbler: {
    aspect: [1.1, 1.45],
    mouth: [0.25, 0.45],
    spacing: [0.18, 0.36],
    eyeSize: [0.09, 0.14],
    horns: ["none", "none", "nubs"],
    tails: ["none"],
    ears: ["none"],
    antennae: [0, 1],
    legPairs: [0, 0],
    markings: ["none", "spots", "spots", "patch"],
  },
  glimmer: {
    aspect: [1.0, 1.35],
    mouth: [0.18, 0.3],
    spacing: [0.25, 0.42],
    eyeSize: [0.08, 0.12],
    horns: ["none"],
    tails: ["none"],
    ears: ["none", "fin"],
    antennae: [0, 2],
    legPairs: [0, 0],
    markings: ["none", "spots", "stripes"],
  },
  critter: {
    aspect: [0.95, 1.25],
    mouth: [0.2, 0.4],
    spacing: [0.26, 0.44],
    eyeSize: [0.1, 0.15],
    horns: ["none", "none", "nubs", "spike"],
    tails: ["none", "stinger"],
    ears: ["none"],
    antennae: [1, 3],
    legPairs: [2, 3],
    markings: ["none", "spots", "stripes", "patch"],
  },
  woodwump: {
    aspect: [1.05, 1.3],
    mouth: [0.4, 0.55],
    spacing: [0.34, 0.46],
    eyeSize: [0.11, 0.14],
    horns: ["none"],
    tails: ["curl"],
    ears: ["none"],
    antennae: [0, 0],
    legPairs: [1, 1],
    markings: ["none"],
  },
  acidmite: {
    aspect: [1.15, 1.4],
    mouth: [0.5, 0.62],
    spacing: [0.3, 0.42],
    eyeSize: [0.12, 0.15],
    horns: ["none"],
    tails: ["none"],
    ears: ["none"],
    antennae: [1, 1],
    legPairs: [3, 3],
    markings: ["none"],
  },
  choruspede: {
    aspect: [0.95, 1.1],
    mouth: [0.3, 0.4],
    spacing: [0.3, 0.42],
    eyeSize: [0.12, 0.15],
    horns: ["none"],
    tails: ["none"],
    ears: ["none"],
    antennae: [2, 2],
    legPairs: [1, 1],
    markings: ["none"],
  },
  operatoad: {
    aspect: [1.5, 1.8],
    mouth: [0.5, 0.6],
    spacing: [0.3, 0.4],
    eyeSize: [0.1, 0.13],
    horns: ["none"],
    tails: ["none"],
    ears: ["none"],
    antennae: [0, 0],
    legPairs: [1, 1],
    markings: ["none"],
  },
  drawbear: {
    aspect: [1.0, 1.2],
    mouth: [0.25, 0.35],
    spacing: [0.3, 0.42],
    eyeSize: [0.1, 0.13],
    horns: ["none"],
    tails: ["none"],
    ears: ["round"],
    antennae: [0, 0],
    legPairs: [1, 1],
    markings: ["none"],
  },
  boomer: {
    aspect: [1.35, 1.6],
    mouth: [0.5, 0.65],
    spacing: [0.4, 0.5],
    eyeSize: [0.11, 0.135],
    horns: ["none"],
    tails: ["curl"],
    ears: ["none"],
    antennae: [0, 0],
    legPairs: [1, 1],
    markings: ["none"],
  },
};

const SPECIES_SALT: Record<Species, number> = {
  thumper: 1,
  snapper: 2,
  chirper: 3,
  clapper: 4,
  wobbler: 5,
  glimmer: 6,
  critter: 7,
  woodwump: 8,
  acidmite: 9,
  choruspede: 10,
  operatoad: 11,
  drawbear: 12,
  boomer: 13,
};

/** Okabe-Ito accent hues (no black) in a fixed order. */
const OI_ACCENTS = [
  OKABE_ITO.orange,
  OKABE_ITO.skyBlue,
  OKABE_ITO.bluishGreen,
  OKABE_ITO.yellow,
  OKABE_ITO.blue,
  OKABE_ITO.vermillion,
  OKABE_ITO.reddishPurple,
];

const r3 = (x: number) => Math.round(x * 1000) / 1000;
const clamp = (v: number, lo: number, hi: number) => (v < lo ? lo : v > hi ? hi : v);

/** Pick the form for a species from the seed and traits (see FORM RULE). */
export function pickForm(species: Species, seed: number, traits: CreatureTraits = NEUTRAL_TRAITS): CreatureForm {
  const defs = FORMS[species];
  if (defs.length === 1) return defs[0].form;
  const rng = new Rng((seed ^ Math.imul(SPECIES_SALT[species] + 101, 0x85ebca6b)) >>> 0);
  const weights = defs.map((d) => {
    let s = 0;
    for (const k of Object.keys(d.aff) as (keyof CreatureTraits)[])
      s += (d.aff[k] ?? 0) * (traits[k] - NEUTRAL_TRAITS[k]);
    return Math.max(0.15, 1 + 3 * s);
  });
  const total = weights.reduce((a, b) => a + b, 0);
  let x = rng.float() * total;
  for (let i = 0; i < defs.length; i++) {
    x -= weights[i];
    if (x < 0) return defs[i].form;
  }
  return defs[defs.length - 1].form;
}

function normTraits(t: CreatureTraits): CreatureTraits {
  const u = (v: number) => clamp(Number.isFinite(v) ? v : 0, 0, 1);
  return {
    size: clamp(Number.isFinite(t.size) ? t.size : 1, 0.7, 1.4),
    stature: clamp(Number.isFinite(t.stature) ? t.stature : 0, -1, 1),
    colorfulness: u(t.colorfulness),
    spikiness: u(t.spikiness),
    fuzz: u(t.fuzz),
    brightness: u(t.brightness),
    tail: u(t.tail),
    width: u(t.width),
    glow: u(t.glow),
    wobble: u(t.wobble),
  };
}

/**
 * Generate the genome for a species and seed. Deterministic: same inputs → deep-equal output.
 * The seed is usually `creatureSeed(projectId, pad)`; `traits` usually `traitsForPad(...)`.
 * `form` forces a body plan (species override UIs, gallery); it must belong to the species.
 */
export function generateGenome(
  species: Species,
  seed: number,
  traits: CreatureTraits = NEUTRAL_TRAITS,
  form?: CreatureForm,
): CreatureGenome {
  const tr = normTraits(traits);
  const formDefs = FORMS[species];
  const chosen = form && formDefs.some((d) => d.form === form) ? form : pickForm(species, seed, tr);
  // Mix the species into the seed so the same pad seed gives an unrelated individual per species.
  // All random draws happen in a fixed order (independent of traits) so traits act monotonically.
  const rng = new Rng((seed ^ Math.imul(SPECIES_SALT[species], 0x9e3779b1)) >>> 0);
  const plan = PLANS[species];
  const family = SPECIES_COLORS[species];
  const synth = SYNTH_SPECIES.includes(species);

  // Colors: a family member, nudged a little in hue and lightness (stays recognisably the species).
  // Synth families are [material, material tint, material shade, instrument accent].
  let base = rng.pick(synth ? family.slice(0, 3) : family);
  base = hueShift(base, rng.range(-5, 5));
  base = rng.chance(0.5) ? mix(base, 0xffffff, rng.range(0, 0.12)) : mix(base, 0x000000, rng.range(0, 0.1));
  const accentA = rng.chance(0.6);
  const accentT = rng.range(0.22, 0.38),
    accentT2 = rng.range(0.05, 0.2);
  const accent = synth ? family[3] : accentA ? mix(base, 0x1e1a2e, accentT) : mix(family[2], 0x1e1a2e, accentT2);
  const bellyT = rng.range(0.4, 0.6);

  // Palette: Okabe-Ito accents away from the species hue, in seeded order.
  const order = OI_ACCENTS.map((c) => ({ c, k: rng.float() }))
    .sort((a, b) => a.k - b.k)
    .map((o) => o.c);
  const canonical = hueOfColor(family[synth ? 3 : 0]);
  const candidates = order.filter(
    (c) => hueDistance(hueOfColor(c), canonical) > 28 && hueDistance(hueOfColor(c), hueOfColor(base)) > 28,
  );
  const nPal = Math.min(3, Math.floor(tr.colorfulness * 3.5), candidates.length);
  const palette = candidates.slice(0, nPal);

  const flat = tr.colorfulness < 0.15;
  const belly = mix(base, 0xffffff, flat ? 0.18 : bellyT);

  // Markings: richer with colorfulness.
  const markPick = rng.pick(plan.markings);
  const baseSpots = rng.int(2, 6);
  const spotPool: MarkSpot[] = [];
  for (let i = 0; i < 9; i++) {
    // Keep spots in the upper/side region of the body so they don't collide with the face.
    const a = rng.range(0, Math.PI * 2);
    const d = rng.range(0.45, 0.8);
    spotPool.push({ x: r3(Math.cos(a) * d), y: r3(Math.sin(a) * d * 0.8), r: r3(rng.range(0.05, 0.1)) });
  }
  const stripeN = rng.int(2, 4),
    stripeTilt = r3(rng.range(-0.35, 0.35));
  const patch: MarkSpot = { x: r3(rng.range(-0.5, 0.5)), y: r3(rng.range(-0.6, -0.2)), r: r3(rng.range(0.14, 0.22)) };
  // Very colourful creatures always get some markings.
  const markKind: MarkingKind = flat
    ? "none"
    : markPick === "none" && tr.colorfulness >= 0.6 && plan.markings.length > 1
      ? plan.markings[1]
      : markPick;
  const markings: Markings = {
    kind: markKind,
    spots: [],
    stripes: 0,
    stripeTilt: 0,
    multi: !flat && palette.length > 0 && tr.colorfulness >= 0.45,
  };
  if (markKind === "spots")
    markings.spots = spotPool.slice(0, Math.min(9, baseSpots + Math.round(tr.colorfulness * 3)));
  else if (markKind === "stripes") {
    markings.stripes = Math.min(5, stripeN + (tr.colorfulness >= 0.7 ? 1 : 0));
    markings.stripeTilt = stripeTilt;
  } else if (markKind === "patch") markings.spots.push(patch);

  const aspect0 = rng.range(plan.aspect[0], plan.aspect[1]);
  const eye0 = rng.range(plan.eyeSize[0], plan.eyeSize[1]);
  const spacing = rng.range(plan.spacing[0], plan.spacing[1]);
  const eyeHeight = rng.range(-0.08, 0.08);
  const pupil = rng.range(0.38, 0.7);
  const cyclops = rng.chance(0.12);
  const mouth = rng.range(plan.mouth[0], plan.mouth[1]);
  const blushRoll = rng.chance(0.45);
  let horns = rng.pick(plan.horns);
  const antennae = rng.int(plan.antennae[0], plan.antennae[1]);
  let tail = rng.pick(plan.tails);
  let ears = rng.pick(plan.ears);
  const claw = rng.range(0.75, 1.35);
  const beak = rng.range(0.6, 1.5);
  const tendrils0 = rng.int(3, 5);
  const legPairs = rng.int(plan.legPairs[0], plan.legPairs[1]);

  // Trait overrides.
  if (tr.spikiness >= 0.6 && horns !== "spike" && plan.horns.includes("spike")) horns = "spike";
  if (tr.spikiness < 0.1 && horns === "spike") horns = plan.horns.includes("nubs") ? "nubs" : "none";
  if (tr.spikiness >= 0.65 && plan.ears.includes("pointy")) ears = "pointy";
  if (tr.tail >= 0.65 && tail === "none") tail = plan.tails.find((t) => t !== "none") ?? "none";

  const brightness = tr.brightness;
  const genome: CreatureGenome = {
    species,
    form: chosen,
    seed: seed >>> 0,
    traits: tr,
    sizeScale: r3(tr.size),
    heightScale: r3(1 + 0.17 * tr.stature),
    limbLength: r3(1 + 0.4 * tr.stature),
    span: r3(0.75 + 0.7 * tr.width),
    tailLength: r3(0.6 + tr.tail),
    baseColor: base,
    accentColor: accent,
    bellyColor: belly,
    palette,
    cheekColor: palette.length > 0 && tr.colorfulness >= 0.45 ? mix(palette[0], 0xffffff, 0.25) : 0xff7f9f,
    bodyAspect: r3(clamp(aspect0 * (1 - 0.3 * tr.stature), GENOME_RANGES.bodyAspect[0], GENOME_RANGES.bodyAspect[1])),
    eyeSize: r3(eye0 * (0.8 + 0.5 * brightness)),
    eyeSpacing: r3(spacing),
    eyeHeight: r3(eyeHeight),
    pupilSize: r3(pupil),
    eyeCount: (species === "critter" || species === "wobbler") && cyclops ? 1 : 2,
    sparkles: brightness < 0.35 ? 0 : brightness < 0.6 ? 1 : brightness < 0.82 ? 2 : 3,
    lidRest: r3(clamp((0.25 - brightness) * 1.4, 0, 0.35)),
    mouthWidth: r3(mouth),
    teeth: tr.spikiness >= 0.55,
    blush: !flat && (blushRoll || tr.colorfulness >= 0.6),
    markings,
    horns,
    spikes: tr.spikiness < 0.35 ? 0 : Math.min(7, Math.round(2 + (tr.spikiness - 0.35) * 8)),
    fuzz: r3(tr.fuzz),
    mohawk: tr.spikiness >= 0.7 && tr.fuzz >= 0.5,
    antennae,
    tail,
    ears,
    clawSize: species === "snapper" ? r3(claw) : 1,
    beakLength: species === "chirper" ? r3(beak) : 1,
    tendrils: species === "glimmer" ? tendrils0 + Math.round(tr.tail * 2) : 0,
    legPairs,
    aura: r3(tr.glow),
    wobble: r3(tr.wobble),
    shimmer: tr.colorfulness >= 0.65 && tr.glow >= 0.5 && palette.length > 0,
  };
  return genome;
}

/** Number of distinct fill colours a genome uses (base, belly, accent markings, palette). */
export function genomeColorCount(g: CreatureGenome): number {
  const s = new Set<number>([g.baseColor]);
  if (g.bellyColor !== g.baseColor) s.add(g.bellyColor);
  if (g.markings.kind !== "none") s.add(g.accentColor);
  for (const c of g.palette) s.add(c);
  return s.size;
}

function hueOfColor(c: number): number {
  return hueOf(c);
}

/** Hue of a 0xRRGGBB color in degrees (0..360). Exported for tests and tinting helpers. */
export function hueOf(c: number): number {
  const r = ((c >> 16) & 255) / 255,
    g = ((c >> 8) & 255) / 255,
    b = (c & 255) / 255;
  const max = Math.max(r, g, b),
    min = Math.min(r, g, b);
  const d = max - min;
  if (d === 0) return 0;
  let h: number;
  if (max === r) h = ((g - b) / d) % 6;
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  return (h * 60 + 360) % 360;
}

/** Smallest angular distance between two hues in degrees. */
export function hueDistance(a: number, b: number): number {
  const d = Math.abs(a - b) % 360;
  return d > 180 ? 360 - d : d;
}
